import {Component, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {IsGranted} from '../../../../../../decorator/is-granted';
import {MetaTitle} from '../../../../../../decorator/meta-title';
import {Asset} from '../../../../../../models/cms/asset';
import {LoaderService} from '../../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../../services/snackbar/snackbar.service';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {AssetService} from '../../../../../../services/cms/asset.service';

import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {AuthService} from '../../../../../../services/auth/auth.service';
import {AssetTypes} from '../../../../../../constants/asset-type';
import {UploadFile} from '../../../../../../models/upload-file';
import {UploadFileService} from '../../../../../../services/upload-file/upload-file.service';
import {DocumentService} from '../../../../../../services/document/document.service';
import {ImageService} from '../../../../../../services/image/image.service';
import {ProductService} from '../../../../../../services/product/product.service';
import {Product} from '../../../../../../models/product';
import {LangService} from '../../../../../../services/language/lang.service';
import {Language} from '../../../../../../models/language';
import {forkJoin} from 'rxjs';
import {CodeEditor} from '@acrodata/code-editor';
import {Extension} from '@codemirror/state';
import {html} from '@codemirror/lang-html';
import {css} from '@codemirror/lang-css';
import {javascript} from '@codemirror/lang-javascript';
import {oneDark} from '@codemirror/theme-one-dark';

@Component({
  selector: 'app-content-library-form',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterLink,
    CodeEditor
],
  templateUrl: './content-library-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './content-library-form.component.scss'
})
@IsGranted(['ASSET_VIEW'])
@MetaTitle('Content Library Form')
export class ContentLibraryFormComponent implements OnInit {
  id: number | null = null;
  loaded = false;
  asset: Asset | null = null;
  assetTypes = AssetTypes;
  uploadFile: UploadFile | null = null;
  editorContent: string = '';
  editorExtensions: Extension[] = [html(), oneDark];
  products: Product[] = [];
  languages: Language[] = [];

  form = new FormGroup({
    name: new FormControl('', Validators.required),
    type: new FormControl('', Validators.required),
    productId: new FormControl<number | null>(null, Validators.required),
    lang: new FormControl<string | null>(null),
  });

  constructor(
    protected imageService: ImageService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private assetService: AssetService,
    private uploadFileService: UploadFileService,
    private documentService: DocumentService,
    private productService: ProductService,
    private langService: LangService,
  ) {
  }

  ngOnInit() {
    this.id = Number(this.route.snapshot.paramMap.get('id'));

    if (!this.id) {
      this.router.navigateByUrl('/promotion-tools/cms/content-library').then();
      return;
    }

    forkJoin([
      this.productService.findAll({sort: 'name'}),
      this.langService.getLanguages(),
    ]).subscribe(([products, languages]) => {
      this.products = products;
      this.languages = languages;
    });

    this.refresh();
  }

  refresh(): void {
    if (this.id) {
      this.loader.startLoading();
      this.assetService.findOneById(this.id).subscribe(asset => {
        this.asset = asset;

        this.form.patchValue({
          name: this.asset.name,
          type: this.asset.type,
          productId: this.asset.product?.id ?? null,
          lang: this.asset.lang,
        });

        this.updateEditorExtensions();

        if (this.isTextAsset() && this.asset.document?.id) {
          this.loadDocumentContent(this.asset.document.id);
        } else {
          this.loader.stopLoading();
          this.loaded = true;
        }
      });
    }
  }

  loadDocumentContent(documentId: number): void {
    this.documentService.getContent(documentId).subscribe({
      next: (result) => {
        this.editorContent = atob(result.content);
        this.loader.stopLoading();
        this.loaded = true;
      },
      error: () => {
        this.loader.stopLoading();
        this.loaded = true;
      },
    });
  }

  isTextAsset(): boolean {
    return ['html', 'css', 'js'].includes(this.asset?.type ?? '');
  }

  isImageAsset(): boolean {
    return this.asset?.type === 'image';
  }

  isVideoAsset(): boolean {
    return this.asset?.type === 'video';
  }

  private updateEditorExtensions(): void {
    const type = this.asset?.type;
    if (type === 'css') {
      this.editorExtensions = [css(), oneDark];
    } else if (type === 'js') {
      this.editorExtensions = [javascript(), oneDark];
    } else {
      this.editorExtensions = [html(), oneDark];
    }
  }

  onSubmit(): void {
    if (!this.form.valid) {
      return;
    }

    const params: any = this.form.value;

    if (this.isTextAsset() && this.editorContent) {
      const extension = this.asset?.type ?? 'html';
      params.document = {
        filename: (this.asset?.document?.filename ?? ('asset.' + extension)),
        data: btoa(this.editorContent),
      };
    } else if (this.uploadFile) {
      params.document = this.uploadFile;
    }

    this.loader.startLoading();

    if (this.asset?.id) {
      this.assetService.update(this.asset.id, params).subscribe(() => {
        this.loader.stopLoading();
        this.snackbar.success('Asset has been modified.');
        this.refresh();
      });
    }
  }

  upload(event: Event): void {
    const target: any = event.target;
    const file = target?.files[0];

    this.uploadFileService.upload(file).subscribe((uploadFile: UploadFile) => {
      this.uploadFile = uploadFile;
    });
  }

  canUpdate(): boolean {
    return this.auth.hasRolesSync(['ROLE_API_ASSET_WRITE']);
  }
}
